import { enqueueSnackbar } from 'notistack';
import { updateGridConfig } from 'src/services/AGGridConfiguration.service';

export const parseProgress = (progressString: string): number => {
  if (typeof progressString === 'string') {
    const cleanedString = progressString.replace('%', '').trim();
    return Number(cleanedString) || 0;
  }
  return Number(progressString) || 0;
};

const AMI_VALUE_PATTERN = /^(\d*\.?\d+)\s*%?$/;
const AMI_FRACTION_LIMIT = 3;

const roundAmi = (value: number): number => Math.round(value * 10000) / 10000;

export const parseAmiValue = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const match = String(value).trim().match(AMI_VALUE_PATTERN);
  if (!match) {
    return null;
  }

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

export const amiComparator = (a: unknown, b: unknown): number => {
  const aValue = parseAmiValue(a);
  const bValue = parseAmiValue(b);

  if (aValue === null && bValue === null) {
    return String(a ?? '').localeCompare(String(b ?? ''));
  }
  if (aValue === null) {
    return 1;
  }
  if (bValue === null) {
    return -1;
  }
  if (aValue !== bValue) {
    return aValue - bValue;
  }

  return String(a ?? '').localeCompare(String(b ?? ''));
};

// ENG-3598: the Set Filter compares the formatted value and the typed input
// with a substring match. Emit a canonical numeric token so "30", ".3" and
// "30%" all match "30%", while "30" does not match "130%".
export const amiFilterTextFormatter = (text: unknown): string => {
  if (text === null || text === undefined) {
    return '';
  }

  const raw = String(text).trim();
  const value = parseAmiValue(raw);
  if (value === null) {
    return raw;
  }

  const isFraction =
    !raw.includes('%') && raw.includes('.') && value < AMI_FRACTION_LIMIT;
  const percent = roundAmi(isFraction ? value * 100 : value);
  const fraction = roundAmi(percent / 100);
  const percentText = String(percent);
  const suffix = raw.includes('%') ? '%' : '';

  return `|${percentText}|${fraction}|${percentText}|${suffix}`;
};

export const currencyFormatter = (currency: number): string => {
  const currencyDec = currency ? currency.toFixed(2) : '';
  const formatted = currencyDec.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return currencyDec.length ? `$${formatted}` : null;
};

export const addThousandsComma = (number: number): string => {
  return number ? String(number).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : null;
};

export const dateComparator = (filterDate: Date, cellValue: string): number => {
  if (!cellValue || !filterDate) {
    return -1;
  }

  const dateParts = cellValue.split('-');
  if (dateParts.length !== 3) {
    return -1;
  }

  const [year, month, day] = dateParts.map(Number);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return -1;
  }

  const cellUTC = Date.UTC(year, month - 1, day);
  const filterUTC = Date.UTC(
    filterDate.getFullYear(),
    filterDate.getMonth(),
    filterDate.getDate()
  );
  return cellUTC - filterUTC;
};

export const dateFormatter = (date: string): string => {
  if (!date) {
    return '';
  }

  const dateParts = date.split('-');
  if (dateParts.length !== 3) {
    return date;
  }
  const [year, month, day] = dateParts;
  return `${month}/${day}/${year}`;
};

export const clearFilterModelString = async (
  gridConfigId: number
): Promise<void> => {
  try {
    await updateGridConfig(gridConfigId, { filter_model_string: null });
    enqueueSnackbar('Filters cleared successfully', {
      variant: 'success',
    });
  } catch (e) {
    console.error('ERROR', e);
    enqueueSnackbar('Something went wrong clearing the filters', {
      variant: 'error',
    });
  }
};

// Filter out emergency contacts from the household size count
export const getFilteredHouseholdSize = (
  householdMembers: Array<{
    member_type?: {
      name?: string | null;
    };
  }>
): number => {
  if (!householdMembers || !Array.isArray(householdMembers)) {
    return 0;
  }

  return householdMembers.filter(
    (member) =>
      member.member_type?.name !== 'non-household member emergency contact'
  ).length;
};
